import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOrders } from "./hooks/useOrders";
import OrderFilters from "./components/OrderFilters";
import OrderTable from "./components/OrderTable";
// import { OrderList } from "./types/orderTypes";
import { usePaymentTheme } from "../home/respayment/hooks/usePaymentTheme";
import OrderDetails from "./components/order-details/OrderDetails";
import OrderPagination from "./components/OrderPagination";
import ThermalPrinterTest from "src/print-configuration/PrintConfigration";
import LoadingScreen from "src/shared/components/LoadingScreen";
import { ToastContainer } from "react-toastify";
import useResPrintData from "../home/respayment/useResPrintData";
import { Order } from "src/shared/services/interfacev2";
import { pageTitle } from "src/shared/utils/brand";

// Constants
const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;
const PRINT_TIMEOUT = 500;

const Resorders: React.FC = () => {
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showPrintContent, setShowPrintContent] = useState(false);
  const [viewport, setViewport] = useState({
    isMobile: window.innerWidth < MOBILE_BREAKPOINT,
    isTablet: window.innerWidth < TABLET_BREAKPOINT
  });
  const [page, setPage] = useState(0);
  const { orderList, loading, error, paginationData, searchTerm, selectedOption, selectedDate, selectedDate2, handleSearch, handleFilterChange, applyCustomDateFilter,
     setSelectedDate, setSelectedDate2, handlePageChange, handleRowsPerPageChange, handleImport, handleDownload, } = useOrders();
  const { fontClass, getBgClass, getLightBgClass, sidebarThemeClass } = usePaymentTheme();
  
  // Use the custom hook for print data
  const printData = useResPrintData(selectedOrder);
  
  const rowsPerPage = paginationData.pageSize;


  // Effects
  useEffect(() => {
    document.title = pageTitle("Orders Management");

    const handleResize = () => {
      setViewport({
        isMobile: window.innerWidth < MOBILE_BREAKPOINT,
        isTablet: window.innerWidth < TABLET_BREAKPOINT
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Event handlers
  const handlePageChangeLocal = useCallback((newPage: number) => {
    setPage(newPage);
    handlePageChange(newPage);
  }, [handlePageChange]);

  const handleRowsPerPageChangeLocal = useCallback((newRowsPerPage: number) => {
    setPage(0);
    handleRowsPerPageChange(newRowsPerPage);
  }, [handleRowsPerPageChange]);

  const handlePrintPreview = useCallback((order: Order) => {
    setSelectedOrder(order);
    setShowPrintContent(true);
    
    const printTimer = setTimeout(() => {
      // window.print();
      setShowPrintContent(false);
    }, PRINT_TIMEOUT);

    return () => clearTimeout(printTimer);
  }, []);

  const handleExport = useCallback(async (format: "CSV" | "PDF") => {
    await handleDownload(format, orderList);
  }, [handleDownload, orderList]);

  const handleFileImport = useCallback(async (file: File) => {
    await handleImport(file);
  }, [handleImport]);

  const handleOrderSelect = useCallback((order: Order) => {
    setSelectedOrder(order);
  }, []);

  const handleCloseOrderDetails = useCallback(() => {
    setSelectedOrder(null);
  }, []);

  const handlePrintClose = useCallback((isClosed: boolean) => {
    if (isClosed) {
      setShowPrintContent(false);
    }
  }, []);

  const filterState = useMemo(() => ({ searchQuery: searchTerm, filter: selectedOption, selectedDate, selectedDate2 }), [searchTerm, selectedOption, selectedDate, selectedDate2]);

  const updateFilter = useCallback((updates: Partial<typeof filterState>) => {
    if (updates.searchQuery !== undefined) {
      handleSearch(updates.searchQuery);
    }
    if (updates.filter !== undefined) {
      handleFilterChange(updates.filter);
    }
    if (updates.selectedDate !== undefined) {setSelectedDate(updates.selectedDate); }
    if (updates.selectedDate2 !== undefined) {setSelectedDate2(updates.selectedDate2);}
  }, [handleSearch, handleFilterChange, setSelectedDate, setSelectedDate2]);

  // Computed values
  const orderListPanelWidth = selectedOrder ? "lg:w-2/3" : "w-full";
  const hasOrderDetails = !!selectedOrder;

  return (
    <div className={`flex flex-col h-full p-0.5 overflow-hidden ${fontClass}`}>
      <div className={`thin-scrollbar w-full flex-1 flex flex-col lg:flex-row gap-1 shadow-inner rounded-lg p-1 h-full overflow-auto ${sidebarThemeClass}`}>
        {/* Left Panel - Orders List */}
        <div className={`${orderListPanelWidth} h-full flex flex-col rounded-lg transition-all duration-300`}>
          <div className={`${getBgClass()} px-2 py-2.5 rounded-t-xl flex-shrink-0`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <h1 className="text-lg lg:text-xl font-bold tracking-wide text-white">
                Order List
              </h1>
              {!hasOrderDetails && paginationData && (
                <div className="text-white text-lg font-medium">
                  Total: {paginationData.totalRecords.toLocaleString()} orders
                </div>
              )}
            </div>
          </div>

          <div className={`border-b border-gray-200 ${getLightBgClass()} ${sidebarThemeClass} flex-shrink-0`}>
            <OrderFilters
              filterState={filterState}
              onFilterChange={updateFilter}
              onImport={handleFileImport}
              onExport={handleExport}
              loading={loading}
              showCustomDateApply
              onApplyCustomDate={applyCustomDateFilter}
            />
          </div>

          <div className={`thin-scrollbar flex-1 min-h-0 overflow-auto ${sidebarThemeClass}`}>
            <OrderTable
              orders={orderList}
              loading={loading}
              error={error}
              page={page}
              rowsPerPage={rowsPerPage}
              totalRecords={paginationData?.totalRecords || 0}
              onPageChange={handlePageChangeLocal}
              onRowsPerPageChange={handleRowsPerPageChangeLocal}
              onOrderSelect={handleOrderSelect}
            />
            {paginationData && (
              <div className="flex justify-end -mt-4 mr-6">
                <OrderPagination 
                  paginationData={paginationData} 
                  page={page} 
                  rowsPerPage={rowsPerPage} 
                  onPageChange={handlePageChangeLocal} 
                  onRowsPerPageChange={handleRowsPerPageChangeLocal} />
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Order Details */}
        {hasOrderDetails && (
          <div className="w-full lg:w-1/3 h-full flex flex-col bg-white rounded-lg transition-all duration-300 relative">
            {viewport.isMobile ? (
              <div className="flex items-center justify-between p-1 border-b border-gray-200 bg-white rounded-t-xl">
                <h3 className="text-lg font-semibold text-gray-900">Order Details</h3>
                <button onClick={handleCloseOrderDetails} className="p-2 rounded-lg bg-red-400 text-white hover:opacity-80 transition-colors duration-200 shadow-lg" title="Close details">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="absolute top-2 right-2 -m-2">
                <button onClick={handleCloseOrderDetails} className="p-2 rounded-lg bg-red-400 text-white hover:opacity-80 transition-colors duration-200 shadow-lg" title="Close details">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            <div className="thin-scrollbar flex-1 overflow-auto">
              <OrderDetails selectedOrder={selectedOrder} onPrint={handlePrintPreview} />
            </div>
          </div>
        )}
      </div>

      {loading && <LoadingScreen />}
      <ToastContainer autoClose={1000} />

      {/* Print Content */}
      {showPrintContent && printData && (
        <ThermalPrinterTest PrintData={printData} isClosed={handlePrintClose} openPrint="true" />
      )}

      {/* Mobile overlay */}
      {viewport.isMobile && hasOrderDetails && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={handleCloseOrderDetails} aria-hidden="true" />
      )}
    </div>
  );
};

export default Resorders;